import { onBeforeUnmount, onMounted, unref, watch, type Ref } from 'vue'

export interface AiraGazePoint {
  x: number
  y: number
}

interface UseAiraGazeOptions {
  /**
   * How far the gaze may travel from the artboard centre, as a share of the
   * canvas radius (0..1). The cursor's angle is preserved exactly; only the
   * distance is compressed into this radius.
   */
  range?: number
  /**
   * Softness of the distance falloff, as a share of the travel radius.
   * 1 means the near field tracks the cursor 1:1 and only far positions saturate.
   * Lower values reach the outer limit sooner.
   */
  falloff?: number
  enabled?: Ref<boolean> | boolean
  /**
   * Viewport point to look at instead of the cursor. While it is set the gaze is
   * recomputed every frame, because the widget itself may be moving.
   */
  target?: Ref<AiraGazePoint | null>
}

/**
 * Makes the Rive artboard look at the cursor anywhere in the viewport.
 *
 * Rive only listens for `mousemove` on its own canvas, so pointer-follow normally
 * stops at the widget's bounds. We listen on `document` and dispatch a synthetic
 * `mousemove` on the canvas instead.
 *
 * The remap is radial, not per-axis: we keep the cursor's angle relative to the
 * artboard centre and rescale only the distance, using the distance from the centre
 * to the viewport edge *along that same ray*. Per-axis normalization skews the angle
 * whenever the widget sits off-centre (it does — bottom right corner), which made
 * Aira look away from the cursor.
 *
 * The canvas itself is `pointer-events: none`, so these synthetic events are the only
 * pointer input Rive ever sees. Otherwise native events would take over as soon as the
 * cursor crossed the canvas bounds and the gaze would snap between two coordinate spaces.
 */
export function useAiraGaze(
  canvasRef: Ref<HTMLCanvasElement | null>,
  options: UseAiraGazeOptions = {},
) {
  const { range = 0.95, falloff = 1, enabled = true, target } = options

  let frame = 0
  let trackingFrame = 0
  let pendingX = 0
  let pendingY = 0
  let reducedMotion: MediaQueryList | null = null

  const isEnabled = () => unref(enabled) && !reducedMotion?.matches

  const flush = () => {
    frame = 0
    const canvas = canvasRef.value
    if (!canvas || !isEnabled()) return

    const rect = canvas.getBoundingClientRect()
    if (!rect.width || !rect.height) return

    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2

    const dx = pendingX - centerX
    const dy = pendingY - centerY
    const distance = Math.hypot(dx, dy)

    if (distance < 0.5) {
      dispatch(canvas, centerX, centerY)
      return
    }

    const unitX = dx / distance
    const unitY = dy / distance

    // Circular radius, so the gaze is not stretched on non-square canvases.
    const radius = (Math.min(rect.width, rect.height) / 2) * range

    // Soft saturation instead of a linear "viewport → radius" squeeze.
    // tanh has slope 1 at zero, so a cursor next to the widget maps almost 1:1
    // (which is what the raw coordinates did well), while distant positions ease
    // into `radius` and never leave the artboard's hit area.
    const travel = radius * Math.tanh(distance / Math.max(radius * falloff, 1))

    dispatch(canvas, centerX + unitX * travel, centerY + unitY * travel)
  }

  const onPointerMove = (event: MouseEvent) => {
    // A locked target wins; the cursor keeps updating nothing until it is released.
    if (target?.value) return
    pendingX = event.clientX
    pendingY = event.clientY
    if (frame) return
    frame = requestAnimationFrame(flush)
  }

  /**
   * While a target is locked the widget may be flying towards it, so both the
   * artboard centre and the target's on-screen position keep changing. Re-aim every
   * frame instead of waiting for pointer events that will not come.
   */
  const trackTarget = () => {
    const point = target?.value
    if (!point) {
      trackingFrame = 0
      return
    }

    pendingX = point.x
    pendingY = point.y
    flush()
    trackingFrame = requestAnimationFrame(trackTarget)
  }

  /** Cursor left the window — look straight ahead again. */
  const onPointerLeave = () => {
    const canvas = canvasRef.value
    if (!canvas || !isEnabled()) return
    const rect = canvas.getBoundingClientRect()
    dispatch(canvas, rect.left + rect.width / 2, rect.top + rect.height / 2)
  }

  onMounted(() => {
    reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    document.addEventListener('mousemove', onPointerMove, { passive: true })
    document.addEventListener('mouseleave', onPointerLeave)

    if (target) {
      watch(
        target,
        (point) => {
          if (point && !trackingFrame) trackingFrame = requestAnimationFrame(trackTarget)
        },
        { immediate: true },
      )
    }
  })

  onBeforeUnmount(() => {
    if (frame) cancelAnimationFrame(frame)
    if (trackingFrame) cancelAnimationFrame(trackingFrame)
    frame = 0
    trackingFrame = 0
    document.removeEventListener('mousemove', onPointerMove)
    document.removeEventListener('mouseleave', onPointerLeave)
    reducedMotion = null
  })
}

function dispatch(canvas: HTMLCanvasElement, clientX: number, clientY: number) {
  canvas.dispatchEvent(
    new MouseEvent('mousemove', { clientX, clientY, bubbles: false, cancelable: false }),
  )
}
